import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { NETLIFY_FUNCTIONS_BASE, NETLIFY_FUNCTIONS } from '../../config/api';

/**
 * Hook pour gérer les connexions bancaires
 *
 * Usage:
 * const {
 *   connections,
 *   loading,
 *   error,
 *   refreshConnections,
 *   createConnection,
 *   deleteConnection
 * } = useBankConnections();
 */

export function useBankConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Récupère toutes les connexions bancaires
   */
  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.get(`${NETLIFY_FUNCTIONS_BASE}/getBankConnections`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setConnections(response.data.connections || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des connexions bancaires:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crée une nouvelle connexion bancaire
   *
   * @param {Object} connectionData - Données de la connexion
   * @param {string} connectionData.bank_name - "Qonto" | "SG Pro"
   * @param {string} connectionData.connection_type - "api" | "file_import"
   * @param {Object} connectionData.credentials - { login, secretKey } pour Qonto
   * @param {string} connectionData.account_name - Nom du compte pour SG Pro
   * @param {string} connectionData.iban - IBAN (optionnel)
   * @returns {Promise<Object>} Connexion créée
   */
  const createConnection = useCallback(async (connectionData) => {
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.post(
        `${NETLIFY_FUNCTIONS_BASE}/createBankConnection`,
        connectionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Rafraîchir la liste des connexions
      await fetchConnections();

      return response.data;
    } catch (err) {
      console.error('Erreur lors de la création de la connexion:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [fetchConnections]);

  /**
   * Supprime une connexion bancaire
   *
   * @param {string} connectionId - ID de la connexion à supprimer
   * @returns {Promise<Object>} Résultat de la suppression
   */
  const deleteConnection = useCallback(async (connectionId) => {
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.delete(
        `${NETLIFY_FUNCTIONS_BASE}/deleteBankConnection?connectionId=${connectionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Rafraîchir la liste des connexions
      await fetchConnections();

      return response.data;
    } catch (err) {
      console.error('Erreur lors de la suppression de la connexion:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [fetchConnections]);

  /**
   * Met à jour les utilisateurs autorisés pour une connexion
   *
   * @param {string} connectionId - ID de la connexion
   * @param {string[]} userIds - IDs des utilisateurs autorisés
   * @returns {Promise<Object>} Résultat de la mise à jour
   */
  const updateAuthorizedUsers = useCallback(async (connectionId, userIds) => {
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.put(
        `${NETLIFY_FUNCTIONS_BASE}/updateBankConnectionUsers`,
        { connectionId, userIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Rafraîchir la liste des connexions
      await fetchConnections();

      return response.data;
    } catch (err) {
      console.error('Erreur lors de la mise à jour des utilisateurs autorisés:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [fetchConnections]);

  /**
   * Met à jour le nom d'une connexion bancaire
   *
   * @param {string} connectionId - ID de la connexion
   * @param {string} connectionName - Nouveau nom (vide pour supprimer)
   * @returns {Promise<Object>} Résultat de la mise à jour
   */
  const updateConnectionName = useCallback(async (connectionId, connectionName) => {
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.patch(
        NETLIFY_FUNCTIONS.UPDATE_BANK_CONNECTION,
        { connectionId, connection_name: connectionName },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Mettre à jour localement sans refetch complet
      setConnections(prev => prev.map(c =>
        c.id === connectionId ? { ...c, connection_name: connectionName?.trim() || null } : c
      ));

      return response.data;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du nom:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Rafraîchir manuellement les connexions
   */
  const refreshConnections = useCallback(() => {
    return fetchConnections();
  }, [fetchConnections]);

  // Charger les connexions au montage du composant
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  return {
    connections,
    loading,
    error,
    refreshConnections,
    createConnection,
    deleteConnection,
    updateAuthorizedUsers,
    updateConnectionName
  };
}

export default useBankConnections;
